import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { ChipModule } from 'primeng/chip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { LinkedInService } from 'src/app/services/linkedin.service';
import { CompaniesService } from 'src/app/services/companies.service';
import { SelectableCompany } from 'src/app/models/company.model';
import { StrategyRouteChipComponent } from 'src/app/components/strategy-route-chip/strategy-route-chip.component';
import { Subject, takeUntil, tap } from 'rxjs';

export interface LinkedInSequence {
  id: number;
  sequenceId: string;
  name: string;
  description: string;
  status: 'active' | 'paused' | 'draft' | 'completed';
  createdAt: Date;
  numSteps: number;
  metrics:any;
  connectionsSent: number;
  messagesSent: number;
  totalMessageStarted: number;
  connectionsAccepted: number;
  meetings:number;
  totalMessageReplies:number;
  steps: LinkedInStep[];
  /** Provider's campaign id; links the sequence to a Messaging Map script. */
  providerCampaignId?: string | number;
  /** Owning company (the sequences table's partition key). */
  companyId?: string;
}

export interface LinkedInStep {
  stepOrder: number;
  type: string;
  title: string;
  templateName: string;
  waitDuration?: number;
  waitUnit?: string;
  hasABTest?: boolean;
  abTestDescription?: string;
  icon: string;
}

@Component({
  selector: 'app-linkedin-sequence-details',
  templateUrl: './linkedin-sequence-details.component.html',
  styleUrls: ['./linkedin-sequence-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    PanelModule,
    DividerModule,
    SkeletonModule,
    TooltipModule,
    ChipModule,
    ToastModule,
    StrategyRouteChipComponent
  ],
  providers: [MessageService]
})
export class LinkedInSequenceDetailsComponent implements OnInit, OnDestroy {
  currentCompany: SelectableCompany | null = null;
  sequenceId: string = '';
  companyId: string = '';
  loading: boolean = true;
  sequence: LinkedInSequence | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private linkedInService: LinkedInService,
    private messageService: MessageService,
    private companiesService: CompaniesService
  ) { }

  ngOnInit() {
    this.sequenceId = this.route.snapshot.params['id'];
    this.companyId = this.route.snapshot.params['companyId'];
    this.setupCompanySubscription();    
  }

  ngOnDestroy(): void {
    // Clean up subscriptions when component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupCompanySubscription(): void {
    // Use companyId from URL if available, otherwise use selected company
    if (this.companyId) {
      // Find the company by ID and set it as current
      const company = this.companiesService.getCompanyById(this.companyId);
      if (company) {
        this.currentCompany = company;
        this.loadSequenceData();
      } else {
        console.warn('Company not found for ID:', this.companyId);
        this.fallbackToSelectedCompany();
      }
    } else {
      this.fallbackToSelectedCompany();
    }
  }

  private fallbackToSelectedCompany(): void {
    // Subscribe to company changes and reload data when company changes
    this.companiesService.selectedCompany$
      .pipe(
        takeUntil(this.destroy$),
        // Remove the filter to see all emissions
        tap(company => console.log('Company emission:', company))
      )
      .subscribe(company => {
        if (company && company.companyId) {
          // Only load if this is a different company
          if (company.companyId !== this.currentCompany?.companyId) {
            console.log('Company changed from', this.currentCompany?.companyId, 'to', company.companyId);
            this.currentCompany = company;
            this.companyId = company.companyId;
            this.loadSequenceData()
          }
        } else {
          console.log('No valid company received:', company);
          this.currentCompany = null;
        }
      });
  }
  private async loadSequenceData() {
    try {
      const result = await this.linkedInService.getLinkedInSequence(this.sequenceId,this.currentCompany!.companyId);
      this.sequence = result.data.sequence;
      this.loading = false;
      console.log(this.sequence);
      //@ts-ignore
      this.sequence.steps = this.sequence?.steps.sort((a, b) => a.stepOrder - b.stepOrder);
    } catch (e) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load data'
      });
    }
  }

  // private loadMockSequence() {
  //   // Mock sequence data - replace with actual API call
  //   this.sequence = {
  //     id: 1,
  //     sequenceId: this.sequenceId,
  //     name: 'Tech Startup Outreach',
  //     description: 'Connecting with tech startup founders and CTOs',
  //     status: 'active',
  //     createdAt: new Date('2024-01-15'),
  //     numSteps: 3,
  //     totalSent: 150,
  //     connections: 89,
  //     responses: 23,
  //     meetings: 12,
  //     steps: [
  //       {
  //         stepNumber: 1,
  //         type: 'connection',
  //         title: 'Connection Request',
  //         templateName: 'template_1',
  //         waitDuration: 0,
  //         waitUnit: 'immediately',
  //         hasABTest: false,
  //         icon: 'pi-user-plus'
  //       },
  //       {
  //         stepNumber: 2,
  //         type: 'message',
  //         title: 'Direct Message',
  //         templateName: 'template_2',
  //         waitDuration: 3,
  //         waitUnit: 'days',
  //         hasABTest: true,
  //         abTestDescription: 'Testing variant A against alternative content',
  //         icon: 'pi-comment'
  //       },
  //       {
  //         stepNumber: 3,
  //         type: 'inmail',
  //         title: 'InMail',
  //         templateName: 'template_3',
  //         waitDuration: 1,
  //         waitUnit: 'day',
  //         hasABTest: false,
  //         icon: 'pi-envelope'
  //       }
  //     ]
  //   };
  // }

  goBack() {
    this.router.navigate(['/linkedin']);
  }

  pauseSequence() {
    // Implement pause sequence logic
    console.log('Pausing sequence:', this.sequence?.name);
  }

  getStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' {
    if (!this.sequence) return 'info';
    switch (this.sequence.status) {
      case 'active': return 'success';
      case 'paused': return 'warn';
      case 'draft': return 'info';
      case 'completed': return 'info';
      default: return 'info';
    }
  }

  getStepIcon(step: LinkedInStep): string {
    return step.icon;
  }

  getWaitText(step: LinkedInStep): string {
    if (step.waitDuration === 0 || step.waitUnit === 'immediately') {
      return 'Wait: Immediately';
    }
    return `Wait: ${step.waitDuration} ${step.waitUnit}`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }

  capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}